'use client'

import { ProductsConnection, ProductsConnectionEdge } from '@/eshop/gql/graphql'
import { useK2Setup } from '@/eshop/hooks/useK2Setup'
import { getProductsForDocuments } from '@/eshop/queries'
import { Result } from '@swan-io/boxed'
import { useQuery } from '@tanstack/react-query'
import clsx from 'clsx'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { ProductListItemRow } from '../ProductList/ListItem'
import Link from 'next/link'
import { useTranslation } from 'react-i18next'
import Pagination from '../Pagination'
import EshopSearch from '../Search'
import React from 'react'

const PRODUCTS_ON_PAGE = 20

function getProductName(p: ProductsConnectionEdge, languageId: number) {
    const groupName = p.node.productGroup?.sortingNameLang
    const productLang = p.node.productLangItems.find(
        (pl) => pl.languageId === languageId
    )
    const out = groupName ? [groupName] : []
    if (productLang?.CFQuality) {
        out.push(productLang.CFQuality)
    }

    return out.join(', ')
}

function filterProducts(
    products: ProductsConnectionEdge[],
    languageId: number
): ProductsConnectionEdge[] {
    const cache = new Set<string>()
    return products.reduce((acc, p) => {
        const name = getProductName(p, languageId)
        if (!cache.has(name)) {
            acc.push(p)
            cache.add(name)
        }
        return acc
    }, [] as ProductsConnectionEdge[])
}

function getTotalPages(dataLength: number) {
    return Math.ceil(dataLength / PRODUCTS_ON_PAGE)
}

interface Props {
    initialProducts: ProductsConnectionEdge[]
    letter: string
}

export default function EshopDocumentsSection({
    letter,
    initialProducts,
}: Props) {
    const k2 = useK2Setup()
    const [products, setProducts] = useState<ProductsConnectionEdge[]>(
        filterProducts(initialProducts || [], k2.languageId)
    )
    const [page, setPage] = useState(1)
    const [total, setTotal] = useState(getTotalPages(products.length || 0))

    const { t } = useTranslation()

    const { data, isFetched, isLoading, isFetching } = useQuery({
        queryKey: ['documents', letter],
        queryFn: () =>
            getProductsForDocuments()(letter, k2.languageId)
                .mapOk((p) => p.products.edges || [])
                .toPromise(),
        initialData: Result.Ok(products),
    })

    useEffect(() => {
        if (data && data.match) {
            data.match({
                Ok: (v) => {
                    const newProducts = filterProducts(v, k2.languageId)
                    setTotal(getTotalPages(newProducts.length))
                    setPage(1)
                    setProducts(newProducts)
                },
                Error: () => setProducts([]),
            })
        } else if (data && data.tag && data.tag === 'Ok') {
            const newProducts = filterProducts(data.value, k2.languageId)
            setTotal(getTotalPages(newProducts.length))
            setPage(1)
            setProducts(newProducts)
        }
    }, [data, isFetched, k2.languageId])

    const pagedProducts = useMemo(
        () =>
            products.filter((p, i) => {
                const from = (page - 1) * PRODUCTS_ON_PAGE
                const to = page * PRODUCTS_ON_PAGE

                return i >= from && i < to
            }),
        [page, products]
    )

    const isLoadingInProgress = isFetching && !isFetched

    return (
        <div>
            <div className="mt-10 pb-20">
                {isLoadingInProgress && (
                    <div className="flex justify-center my-10">
                        <span className="loading loading-spinner loading-lg"></span>
                    </div>
                )}
                {!isLoadingInProgress &&
                    pagedProducts.length > 0 &&
                    pagedProducts.map((p) => {
                        const safety = p.node.safetyDataSheets?.find(
                            (s) => s?.languageId === k2.languageId
                        )?.url
                        const specification = p.node.specifications?.find(
                            (s) => s?.languageId === k2.languageId
                        )?.url

                        return (
                            <React.Fragment key={p.node.id}>
                                <ProductListItemRow
                                    name={getProductName(p, k2.languageId)}
                                    rightAdornment={
                                        <div className="flex gap-4 text-sm text-blue-dark">
                                            <Link
                                                href={`${
                                                    process.env
                                                        .NEXT_PUBLIC_ESHOP_S3
                                                }${safety || ''}`}
                                                className={clsx(
                                                    safety
                                                        ? 'link'
                                                        : 'invisible'
                                                )}
                                            >
                                                {t(
                                                    'eshop.documents.safetySheet'
                                                )}
                                            </Link>
                                            <Link
                                                href={`${
                                                    process.env
                                                        .NEXT_PUBLIC_ESHOP_S3
                                                }${specification || ''}`}
                                                className={clsx(
                                                    specification
                                                        ? 'link'
                                                        : 'invisible'
                                                )}
                                            >
                                                {t(
                                                    'eshop.documents.specification'
                                                )}
                                            </Link>
                                        </div>
                                    }
                                />
                            </React.Fragment>
                        )
                    })}

                {!isLoadingInProgress && pagedProducts.length === 0 && (
                    <div className="flex justify-center font-bold">
                        {t('eshop.documents.noDocuments')}
                    </div>
                )}

                {page > 0 && total > 0 && (
                    <Pagination
                        onPageChange={setPage}
                        current={page}
                        total={total}
                        className="mt-24"
                    />
                )}
            </div>
        </div>
    )
}

const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
interface LettersProps {
    activeLetter: string | null
    onChange: (letter: string | null) => void
}

function EshopDocumentsLetters({ activeLetter, onChange }: LettersProps) {
    const handleToggleLetter = useCallback(
        (l: string) => () => {
            onChange(l)
        },
        [onChange]
    )
    return (
        <div className="flex justify-between gap-2">
            {LETTERS.split('').map((letter) => (
                <span
                    key={letter}
                    className={clsx(
                        'pointer',
                        letter === activeLetter && 'font-bold'
                    )}
                    onClick={handleToggleLetter(letter)}
                >
                    {letter}
                </span>
            ))}
        </div>
    )
}
